import {Request} from "../core/request";
import {AuthHeader} from "../core/token";

export interface FileData {
    ID: number;
    CreatedAt: string; // ISO 8601 格式的时间字符串
    UpdatedAt: string;
    DeletedAt?: string | null;

    name: string;
    index: number;
    isDir: boolean;
    dirDeep: number;
    path: string; // 完整路径
    version: number;
    storagePath?: string; // 物理存储路径（完整路径）
    size: number; // 文件大小（目录为 0）
    coverPDFPath?: string; // 保存为 PDF 的路径
    coverSize?: number;
    hash?: string; // 文件哈希（SHA256）
    UUID?: string;
    fileType?: string;
    MIMEType?: string;
    createdBy: number;
    createdByName: string;
    isUserRoot: boolean;
    notDeleted: boolean;
    deleted: boolean;
    deleteTime?: string | null;
    parentID?: number;

    type: string;
    deep: number;
    readNum: number;
    children?: FileData[];

    CosFileUrl: string;

    CanAuth?: boolean;
    CanPassword?: boolean;

    isShareAuthFile: boolean;
    isShareAuthRoot: boolean;
    shareByName: string;
    fileUUIDByName?: string; // uuid部分对应的人名，用于面包屑显示

    isResignation?: boolean; // 已离职老师
}

export interface DeleteFile extends FileData {
    deleteUUID: string;
}

// 获取当前可获取的所有文件列表
export async function getFiles(parentPath: string, share: boolean) {
    let url = "/teacher/files/?parentPath=" + parentPath
    if (share) {
        url += "&share=" + (share ? "1" : "0")
    }
    return await Request.get(url, {
        headers: AuthHeader(),
    });
}

export async function mkdir(filePath: string) {
    return await Request.post('/academics/files/mkdir', {path: filePath}, {headers: AuthHeader()})
}

export async function uploadFile(formData: any, basePath: string, setUploadProgress: (v: any) => void) {
    return await Request.post('/academics/files/upload', formData, {
        onUploadProgress: (progressEvent) => {
            if (progressEvent.total === undefined) {
                return
            }
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            setUploadProgress(progress);
        },
        headers: {
            ...AuthHeader(),
            'Content-Type': 'multipart/form-data',
        },
    })
}

export async function deleteFileOrFolder(filepath: string) {
    return await Request.post(`/academics/files/delete`, {path: filepath}, {headers: AuthHeader()});
}

export interface CreateShareRequest {
    type: string // "user" | "group" | "password"
    remark: string
    userId: number
    groupId: number
    sharePassword: string
    expiryTime: number
}

export async function fileDetail(id: string) {
    return await Request.get(`/teacher/files/${id}/detail`, {headers: AuthHeader()});
}

export interface RawFileMeta {
    fileName: string;
    mimeType: string;
    totalSize: number;
    chunkSize: number;
    totalChunks: number;
    contentKind: 'binary' | 'text';
    fileUUID: string;
}

export interface FetchRawOptions {
    password?: string;
    password_uuid?: string;
    onProgress?: (loaded: number, total: number) => void;
}

function rawQueryParams(options?: FetchRawOptions): string {
    if (!options?.password && !options?.password_uuid) {
        return '';
    }
    const params = new URLSearchParams();
    if (options.password) {
        params.set('password', options.password);
    }
    if (options.password_uuid) {
        params.set('password_uuid', options.password_uuid);
    }
    const qs = params.toString();
    return qs ? `?${qs}` : '';
}

export async function fileRawMeta(fileId: string, options?: FetchRawOptions): Promise<RawFileMeta> {
    const resp = await Request.get<RawFileMeta>(
        `/teacher/files/${fileId}/raw/meta${rawQueryParams(options)}`,
        { headers: AuthHeader() },
    );
    return resp.data;
}

export async function fileRawChunk(
    fileId: string,
    index: number,
    options?: FetchRawOptions,
): Promise<ArrayBuffer> {
    const resp = await Request.get(
        `/teacher/files/${fileId}/raw/chunks/${index}${rawQueryParams(options)}`,
        {
            headers: AuthHeader(),
            responseType: 'arraybuffer',
        },
    );
    return resp.data as ArrayBuffer;
}

export async function fileBlob(id?: string, orgFile?: boolean, password?: string, password_uuid?: string, setUploadProgress?: (v: any) => void) {
    const response = await Request.get(
        `/teacher/files/${id}/blob?password=${password}&password_uuid=${password_uuid}&orgFile=${orgFile ? "1" : "0"}`,
        {
            headers: AuthHeader(),
            responseType: 'blob',
            onDownloadProgress: (progressEvent) => {
                if (!setUploadProgress) {
                    return;
                }
                if (!progressEvent) {
                    return;
                }
                if (progressEvent.total === undefined) {
                    return
                }
                const progress = Math.round((progressEvent.total * 100) / progressEvent.total)
                setUploadProgress(progress);
            }
        }
    );
    return response.data.arrayBuffer();
}


export async function getFileLogs(
    page: number = 1,
    pageSize: number = 100,
    startTime?: string,
    endTime?: string
) {
    const params: Record<string, string | number> = {page, pageSize};

    if (startTime) params.startTime = startTime;
    if (endTime) params.endTime = endTime;

    return await Request.get('/super_admin/system/file_logs', {
        params,
        headers: AuthHeader(),
    });
}

export async function getDeleteFiles(
    page: number = 1,
    pageSize: number = 100,
    startTime?: string,
    endTime?: string,
) {
    const params: Record<string, string | number> = {page, pageSize};
    if (startTime) params.startTime = startTime;
    if (endTime) params.endTime = endTime;

    return await Request.get('/super_admin/delete_files/', {
        params,
        headers: AuthHeader(),
    });
}

export async function deleteDeleteFiles(uuid: string) {
    return await Request.delete(`/super_admin/delete_files/${uuid}`, {headers: AuthHeader()});
}

export async function downloadDeleteFile(uuid: string, setDownloadProgress: (v: any) => void) {
    return await Request.get(`/super_admin/delete_files/${uuid}/download`, {
        headers: AuthHeader(),
        responseType: 'blob',
        onDownloadProgress: (progressEvent) => {
            if (progressEvent.total === undefined) {
                return
            }
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            setDownloadProgress(progress);
        },
    });
}

export async function renameFile(uuid: string | undefined, newFileName: string) {
    return await Request.put(`/academics/files/rename`, {
        uuid: uuid,
        new_name: newFileName,
    }, {
        headers: AuthHeader(),
    })
}

export async function moveFile(uuid: string | undefined, targetPath: string) {
    return await Request.put(`/academics/files/move`, {
        uuid: uuid,
        target_path: targetPath,
    }, {
        headers: AuthHeader()
    })
}

export async function updateFileIndexs(files: string[]) {
    return await Request.post(`/academics/files/update_index`, {
        file_list: files
    }, {
        headers: AuthHeader()
    })
}


// 权限共享

export interface FileShareAuth {
    ID: number;
    created_at: string;
    updated_at: string;
    deleted_at?: string | null;

    file_id: number;
    file_org_user_id: number;
    file_org_user_uuid: string;
    file_org_name: string;
    user_id: number;
    user_name: string;
}
export async function getShareAuths(fileID: number): Promise<FileShareAuth[]> {
    const resp = await Request.get(`/academics/files/share_auth/${fileID}`, {
        headers: AuthHeader()
    })
    return resp.data;
}


export async function getAllShareAuths(): Promise<FileShareAuth[]> {
    return await Request.get(`/academics/files/share_auth`, {
        headers: AuthHeader()
    })
}

export async function setShareAuth(fileID: number, userId: number ) : Promise<void> {
    return await Request.post(`/academics/files/share_auth`,{
        fileId: fileID,
        userId: userId,
    }, {
        headers: AuthHeader()
    })
}

export async function deleteShareAuth(id: number) : Promise<void> {
    return await Request.delete(`/academics/files/share_auth/${id}`, {
        headers: AuthHeader()
    })
}
